import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CheckCircle2, FileUp, Loader2, UploadCloud, XCircle, Layers, FileText } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { api } from '@/lib/api';
import { uploadDocument } from '@/lib/mutations';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/upload')({
	component: UploadPage,
});

type Mode = 'single' | 'bulk';

function UploadPage() {
	const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
	const isAdmin = meQuery.data?.role === 'admin';
	const [mode, setMode] = useState<Mode>('single');

	if (meQuery.isLoading) return <div className="p-8 text-sm text-muted-foreground">Laden…</div>;

	return (
		<div className="mx-auto max-w-3xl space-y-6">
			<div>
				<h1 className="text-2xl font-semibold tracking-tight">Handmatig uploaden</h1>
				<p className="text-sm text-muted-foreground">
					Upload bestanden direct, bypass van de mail-intake. Dedup, OCR en classificatie werken identiek.
				</p>
			</div>

			{isAdmin && (
				<div className="flex flex-wrap gap-2 border-b">
					<TabButton active={mode === 'single'} onClick={() => setMode('single')} icon={<FileText className="h-4 w-4" />}>
						Eén bestand
					</TabButton>
					<TabButton active={mode === 'bulk'} onClick={() => setMode('bulk')} icon={<Layers className="h-4 w-4" />}>
						Bulk (batch-verwerking)
					</TabButton>
				</div>
			)}

			{mode === 'single' ? <SinglePanel /> : <BulkPanel />}
		</div>
	);
}

function TabButton({
	active,
	onClick,
	icon,
	children,
}: {
	active: boolean;
	onClick: () => void;
	icon: React.ReactNode;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={
				active
					? 'flex items-center gap-1.5 border-b-2 border-primary px-3 py-2 text-sm font-medium text-primary'
					: 'flex items-center gap-1.5 border-b-2 border-transparent px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
			}
		>
			{icon}
			{children}
		</button>
	);
}

function ChannelOption({
	value,
	checked,
	onChange,
	title,
	hint,
}: {
	value: string;
	checked: boolean;
	onChange: () => void;
	title: string;
	hint: string;
}) {
	return (
		<label className="flex cursor-pointer items-start gap-2.5 text-sm">
			<input
				type="radio"
				name="upload-channel"
				value={value}
				checked={checked}
				onChange={onChange}
				className="mt-0.5"
			/>
			<span className="space-y-0.5">
				<span className="block font-medium leading-none">{title}</span>
				<span className="block text-xs text-muted-foreground">{hint}</span>
			</span>
		</label>
	);
}

// ---------------------------------------------------------------------------
// Single-file upload (sync, default)
// ---------------------------------------------------------------------------

function SinglePanel() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [file, setFile] = useState<File | null>(null);
	const [entityCode, setEntityCode] = useState('');
	const [subject, setSubject] = useState('');
	const [channel, setChannel] = useState<'general' | 'inkoop' | 'verkoop'>('general');
	const [dragOver, setDragOver] = useState(false);

	const entitiesQuery = useQuery({ queryKey: ['entities'], queryFn: () => api.entities() });
	const selectedEntity = entitiesQuery.data?.entities.find((e) => e.code === entityCode);

	const uploadMutation = useMutation({
		mutationFn: () => {
			if (!file) throw new Error('Geen bestand gekozen');
			if (!entityCode) throw new Error('Geen entiteit gekozen');
			return uploadDocument({ file, entityCode, subject: subject || undefined, channel });
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['documents'] });
		},
	});

	const handleFiles = (files: FileList | null) => {
		if (!files || files.length === 0) return;
		setFile(files[0] ?? null);
	};

	const reset = () => {
		setFile(null);
		setSubject('');
		setChannel('general');
		uploadMutation.reset();
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle className="text-base">Bestand en entiteit</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-1.5">
						<Label>Entiteit</Label>
						<Select
							value={entityCode}
							onChange={(e) => {
								setEntityCode(e.target.value);
								setChannel('general');
							}}
						>
							<option value="">Kies een entiteit...</option>
							{entitiesQuery.data?.entities.map((e) => (
								<option key={e.code} value={e.code}>
									{e.name}
								</option>
							))}
						</Select>
					</div>

					<div className="space-y-1.5">
						<Label>Bestemming</Label>
						<div className="flex flex-col gap-1.5 rounded-md border p-3">
							<ChannelOption
								value="general"
								checked={channel === 'general'}
								onChange={() => setChannel('general')}
								title="Algemeen archief"
								hint="Alleen opslaan en classificeren — verschijnt niet in de boek-werklijsten."
							/>
							{(selectedEntity?.has_inkoop ?? true) && (
								<ChannelOption
									value="inkoop"
									checked={channel === 'inkoop'}
									onChange={() => setChannel('inkoop')}
									title="Inkoop (Exact)"
									hint="Als inkoopfactuur naar de /inkoopfacturen-werklijst met boekingsvoorstel."
								/>
							)}
							{selectedEntity?.has_verkoop && (
								<ChannelOption
									value="verkoop"
									checked={channel === 'verkoop'}
									onChange={() => setChannel('verkoop')}
									title="Verkoop (Exact)"
									hint="Als verkoopfactuur naar de /verkoopfacturen-werklijst."
								/>
							)}
						</div>
					</div>

					<div className="space-y-1.5">
						<Label>Onderwerp (optioneel)</Label>
						<Input
							placeholder="bv. Factuur Mei 2026"
							value={subject}
							onChange={(e) => setSubject(e.target.value)}
						/>
					</div>

					<div className="space-y-1.5">
						<Label>Bestand</Label>
						<div
							onDragOver={(e) => {
								e.preventDefault();
								setDragOver(true);
							}}
							onDragLeave={() => setDragOver(false)}
							onDrop={(e) => {
								e.preventDefault();
								setDragOver(false);
								handleFiles(e.dataTransfer.files);
							}}
							onClick={() => fileInputRef.current?.click()}
							className={cn(
								'flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-10 text-center transition-colors',
								dragOver ? 'border-primary bg-primary/5' : 'border-border bg-muted/30',
								'cursor-pointer hover:bg-muted/50',
							)}
						>
							<UploadCloud className="h-8 w-8 text-muted-foreground" />
							{file ? (
								<div className="space-y-1 text-sm">
									<div className="font-medium">{file.name}</div>
									<div className="text-xs text-muted-foreground">{Math.round(file.size / 1024)} KB · {file.type || 'PDF'}</div>
								</div>
							) : (
								<div className="space-y-1">
									<div className="text-sm font-medium">Sleep PDF hierheen of klik om te bladeren</div>
									<div className="text-xs text-muted-foreground">Maximaal 25 MB, alleen PDF wordt geclassificeerd</div>
								</div>
							)}
						</div>
						<input
							ref={fileInputRef}
							type="file"
							accept="application/pdf,.pdf"
							hidden
							onChange={(e) => handleFiles(e.target.files)}
						/>
					</div>

					<div className="flex items-center gap-3">
						<Button
							disabled={!file || !entityCode || uploadMutation.isPending}
							onClick={() => uploadMutation.mutate()}
						>
							{uploadMutation.isPending ? (
								<>
									<Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
									Uploaden...
								</>
							) : (
								<>
									<FileUp className="mr-1.5 h-4 w-4" />
									Upload
								</>
							)}
						</Button>
						{file && (
							<Button variant="ghost" size="sm" onClick={reset}>
								Reset
							</Button>
						)}
					</div>
				</CardContent>
			</Card>

			{uploadMutation.isSuccess && uploadMutation.data && (
				<Card className="border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/30">
					<CardContent className="flex items-start gap-3 p-4">
						<CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
						<div className="flex-1 space-y-2 text-sm">
							{uploadMutation.data.status === 'received' && (
								<>
									<p className="font-medium text-emerald-900 dark:text-emerald-100">Document opgeslagen</p>
									<p className="text-xs text-muted-foreground">
										Document staat in de queue voor OCR en classificatie. Duurt normaal enkele seconden.
									</p>
									<div className="flex gap-2 pt-1">
										<Button
											size="sm"
											onClick={() =>
												navigate({ to: '/documents/$id', params: { id: uploadMutation.data!.document_id } })
											}
										>
											Bekijk document
										</Button>
										<Button size="sm" variant="outline" onClick={reset}>
											Nog een uploaden
										</Button>
									</div>
								</>
							)}
							{uploadMutation.data.status === 'duplicate' && (
								<>
									<p className="font-medium">Dubbele upload herkend</p>
									<p className="text-xs text-muted-foreground">
										Dit PDF-bestand is al eerder opgeslagen binnen deze entiteit (zelfde hash). Geen nieuwe rij aangemaakt.
									</p>
									<div className="flex gap-2 pt-1">
										<Button
											size="sm"
											onClick={() =>
												navigate({ to: '/documents/$id', params: { id: uploadMutation.data!.document_id } })
											}
										>
											Bekijk bestaand document
										</Button>
										<Button size="sm" variant="outline" onClick={reset}>
											Nieuw bestand
										</Button>
									</div>
								</>
							)}
							{uploadMutation.data.status === 'skipped' && (
								<>
									<p className="font-medium">Overgeslagen</p>
									<p className="text-xs text-muted-foreground">
										Reden: {uploadMutation.data.reason}. Document is opgeslagen in R2 (skipped-prefix) maar niet geclassificeerd.
									</p>
									<Button size="sm" variant="outline" onClick={reset}>
										Nieuw bestand
									</Button>
								</>
							)}
						</div>
					</CardContent>
				</Card>
			)}

			{uploadMutation.isError && (
				<Card className="border-destructive/40 bg-destructive/5">
					<CardContent className="flex items-start gap-3 p-4">
						<XCircle className="mt-0.5 h-5 w-5 text-destructive" />
						<div className="text-sm">
							<p className="font-medium text-destructive">Upload mislukt</p>
							<p className="mt-1 text-xs text-muted-foreground">
								{uploadMutation.error instanceof Error ? uploadMutation.error.message : 'Onbekende fout'}
							</p>
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Bulk-upload (admin-only, batch-verwerking)
// ---------------------------------------------------------------------------

interface BulkRow {
	file: File;
	status: 'pending' | 'uploading' | 'received' | 'duplicate' | 'skipped' | 'error';
	documentId?: string;
	error?: string;
	skipReason?: string;
}

function BulkPanel() {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [rows, setRows] = useState<BulkRow[]>([]);
	const [entityCode, setEntityCode] = useState('');
	const [dragOver, setDragOver] = useState(false);
	const [busy, setBusy] = useState(false);

	const entitiesQuery = useQuery({ queryKey: ['entities'], queryFn: () => api.entities() });

	const addFiles = (files: FileList | null) => {
		if (!files || files.length === 0) return;
		const next: BulkRow[] = [];
		for (let i = 0; i < files.length; i++) {
			const f = files.item(i);
			if (f) next.push({ file: f, status: 'pending' });
		}
		setRows((prev) => [...prev, ...next]);
	};

	const removeRow = (idx: number) => {
		setRows((prev) => prev.filter((_, i) => i !== idx));
	};

	const reset = () => {
		setRows([]);
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const startUpload = async () => {
		if (!entityCode || rows.length === 0 || busy) return;
		setBusy(true);
		// Sequentieel uploaden zodat dedup-checks consistent draaien en we
		// per-rij feedback kunnen tonen. Volume is laag (paar tientallen
		// bestanden), parallelism levert weinig op.
		for (let i = 0; i < rows.length; i++) {
			const row = rows[i];
			if (!row || row.status !== 'pending') continue;
			setRows((prev) =>
				prev.map((r, idx) => (idx === i ? { ...r, status: 'uploading' as const } : r)),
			);
			try {
				const result = await uploadDocument({
					file: row.file,
					entityCode,
					processingMode: 'batch',
				});
				setRows((prev) =>
					prev.map((r, idx) =>
						idx === i
							? {
									...r,
									status: result.status as BulkRow['status'],
									documentId: result.document_id,
									skipReason: result.reason,
								}
							: r,
					),
				);
			} catch (err) {
				const msg =
					err && typeof err === 'object' && 'body' in err
						? JSON.stringify((err as { body: unknown }).body)
						: err instanceof Error
							? err.message
							: String(err);
				setRows((prev) =>
					prev.map((r, idx) => (idx === i ? { ...r, status: 'error' as const, error: msg } : r)),
				);
			}
		}
		setBusy(false);
	};

	const totalsByStatus = rows.reduce<Record<string, number>>((acc, r) => {
		acc[r.status] = (acc[r.status] ?? 0) + 1;
		return acc;
	}, {});

	return (
		<div className="space-y-6">
			<Card className="border-amber-200 bg-amber-50/50">
				<CardContent className="flex items-start gap-3 p-4 text-xs text-amber-900">
					<Layers className="mt-0.5 h-4 w-4" />
					<div>
						<p className="font-medium">Bulk-upload via Mistral Batch API</p>
						<p>
							OCR + classificatie + embedding draaien in batches op Mistral's batch-endpoint.
							Doorlooptijd doorgaans enkele minuten, max 24 uur SLA. Levert ~50% kostenwinst t.o.v.
							sync. Alleen geschikt voor niet-urgente uploads — voor inkoopfacturen die je vandaag
							nog wilt boeken, gebruik 'Eén bestand' (sync).
						</p>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle className="text-base">Bestanden en entiteit</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-1.5">
						<Label>Entiteit</Label>
						<Select value={entityCode} onChange={(e) => setEntityCode(e.target.value)}>
							<option value="">Kies een entiteit...</option>
							{entitiesQuery.data?.entities.map((e) => (
								<option key={e.code} value={e.code}>
									{e.name}
								</option>
							))}
						</Select>
					</div>

					<div className="space-y-1.5">
						<Label>Bestanden</Label>
						<div
							onDragOver={(e) => {
								e.preventDefault();
								setDragOver(true);
							}}
							onDragLeave={() => setDragOver(false)}
							onDrop={(e) => {
								e.preventDefault();
								setDragOver(false);
								addFiles(e.dataTransfer.files);
							}}
							onClick={() => fileInputRef.current?.click()}
							className={cn(
								'flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-10 text-center transition-colors',
								dragOver ? 'border-primary bg-primary/5' : 'border-border bg-muted/30',
								'cursor-pointer hover:bg-muted/50',
							)}
						>
							<UploadCloud className="h-8 w-8 text-muted-foreground" />
							<div className="space-y-1">
								<div className="text-sm font-medium">Sleep meerdere bestanden hierheen of klik om te bladeren</div>
								<div className="text-xs text-muted-foreground">Maximaal 25 MB per bestand. PDF, JPG, PNG, WEBP.</div>
							</div>
						</div>
						<input
							ref={fileInputRef}
							type="file"
							accept="application/pdf,.pdf,image/*"
							hidden
							multiple
							onChange={(e) => addFiles(e.target.files)}
						/>
					</div>

					{rows.length > 0 && (
						<div className="space-y-2">
							<div className="flex items-center justify-between text-xs text-muted-foreground">
								<span>
									{rows.length} bestand{rows.length === 1 ? '' : 'en'} klaar voor upload
								</span>
								{Object.entries(totalsByStatus).map(([k, v]) => (
									<span key={k}>
										{statusLabel(k as BulkRow['status'])}: {v}
									</span>
								))}
							</div>
							<div className="max-h-[40vh] overflow-y-auto rounded-md border">
								<table className="w-full text-sm">
									<thead className="bg-muted/30 text-xs text-muted-foreground">
										<tr>
											<th className="px-3 py-2 text-left">Bestand</th>
											<th className="px-3 py-2 text-right">Grootte</th>
											<th className="px-3 py-2">Status</th>
											<th className="px-3 py-2" />
										</tr>
									</thead>
									<tbody>
										{rows.map((r, idx) => (
											<tr key={`${r.file.name}-${idx}`} className="border-t">
												<td className="max-w-[16rem] truncate px-3 py-1.5 font-mono text-xs">{r.file.name}</td>
												<td className="px-3 py-1.5 text-right text-xs text-muted-foreground">{Math.round(r.file.size / 1024)} KB</td>
												<td className="px-3 py-1.5">
													<StatusBadge row={r} />
												</td>
												<td className="px-3 py-1.5 text-right">
													{!busy && r.status === 'pending' && (
														<button
															type="button"
															onClick={() => removeRow(idx)}
															className="text-xs text-muted-foreground hover:text-destructive"
														>
															verwijderen
														</button>
													)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}

					<div className="flex items-center gap-3">
						<Button
							disabled={!entityCode || rows.length === 0 || busy}
							onClick={startUpload}
						>
							{busy ? (
								<>
									<Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
									Uploaden…
								</>
							) : (
								<>
									<Layers className="mr-1.5 h-4 w-4" />
									Start bulk-upload
								</>
							)}
						</Button>
						{rows.length > 0 && !busy && (
							<Button variant="ghost" size="sm" onClick={reset}>
								Reset
							</Button>
						)}
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

function StatusBadge({ row }: { row: BulkRow }) {
	if (row.status === 'pending') return <span className="text-xs text-muted-foreground">wachtend</span>;
	if (row.status === 'uploading')
		return (
			<span className="inline-flex items-center gap-1 text-xs text-amber-700">
				<Loader2 className="h-3 w-3 animate-spin" /> uploaden
			</span>
		);
	if (row.status === 'received')
		return (
			<span className="inline-flex items-center gap-1 text-xs text-emerald-700">
				<CheckCircle2 className="h-3 w-3" /> in batch-queue
			</span>
		);
	if (row.status === 'duplicate')
		return <span className="text-xs text-muted-foreground">dubbel — al bekend</span>;
	if (row.status === 'skipped')
		return (
			<span className="text-xs text-muted-foreground" title={row.skipReason}>
				overgeslagen ({row.skipReason ?? 'onbekend'})
			</span>
		);
	return (
		<span className="inline-flex items-center gap-1 text-xs text-destructive" title={row.error}>
			<XCircle className="h-3 w-3" /> fout
		</span>
	);
}

function statusLabel(s: BulkRow['status']): string {
	switch (s) {
		case 'pending':
			return 'wachtend';
		case 'uploading':
			return 'uploaden';
		case 'received':
			return 'in queue';
		case 'duplicate':
			return 'dubbel';
		case 'skipped':
			return 'overgeslagen';
		case 'error':
			return 'fout';
	}
}
